import GridViewIcon from "@mui/icons-material/GridView"
import SearchIcon from "@mui/icons-material/Search"
import TocIcon from "@mui/icons-material/Toc"
import {
	Button,
	Divider,
	Grid,
	IconButton,
	InputBase,
	Paper,
	Tooltip,
} from "@mui/material"
import { styled, useTheme } from "@mui/material/styles"
import {
	ChangeEvent,
	FormEvent,
	FunctionComponent,
	useCallback,
	useEffect,
	useState,
} from "react"
import "react-date-range/dist/styles.css" // main css file
import "react-date-range/dist/theme/default.css" // theme css file
import { useTranslation } from "react-i18next"
import { useInView } from "react-intersection-observer"
import { useDispatch, useSelector } from "react-redux"

import { useNavigate } from "react-router"
import CorporatesList from "../../components/corporates/list"
import CorporateEnhancedTable from "../../components/corporates/table"
import SpinnerCard from "../../components/file/spinner/card"
import FiltersCorporate from "../../components/filters/corporate"
import Sort from "../../components/sort"
import { AppDispatch } from "../../store"
import { BankEssential } from "../../store/actions/bank"
import {
	fetchCorporates,
	getCorporatesPaginated,
} from "../../store/actions/corporates"
import { resetDefault } from "../../store/files-list/files-slice"
import { RootState } from "../../store/reducers"

export interface ICorporatesProperties {}

const CorporatesListView: FunctionComponent<ICorporatesProperties> = () => {
	const navigate = useNavigate()
	const theme = useTheme()
	const { t } = useTranslation()
	const dispatch = useDispatch<AppDispatch>()
	const bankSelectedState: BankEssential | undefined = useSelector(
		(state: RootState) => state.banksUser.selected
	)
	const idBankForPaydoUser: string | undefined = useSelector(
		(state: RootState) => state.banksUser?.list?.[0]?.id
	)
	const authState = useSelector((state: RootState) => state.auth)
	const isAuthenticate = authState?.isAuthenticate
	const corporatesLoadingState: boolean = useSelector(
		(state: RootState) => state.corporatesPaginated.loading
	)
	const corporatesFetchLoadingState = useSelector(
		(state: RootState) => state.corporatesPaginated.loadingFetch
	)
	const corporatenextUrlState = useSelector(
		(state: RootState) => state.corporatesPaginated.list?.nextUrl
	)

	const permissions = useSelector(
		(state: RootState) => state.auth?.user?.permissions
	)

	const hasCrudCorporateEnabled: boolean = permissions
		? permissions.some(
				(perm) =>
					perm.permissionName === "CRUD_CORPORATE" && perm.enabled === true
			)
		: false

	const [sortCriteria, setSortCriteria] = useState([
		{ field: "id", order: "asc" },
	])
	const [filterBank, setFilterBank] = useState<undefined | string>(
		authState.user?.idBank ?? bankSelectedState?.id
	)
	const [whatsappFilter, setWhatsappFilter] = useState<boolean | undefined>(
		undefined
	)
	const [autoAcceptFilter, setAutoAcceptFilter] = useState<boolean | undefined>(
		undefined
	)
	const [checkIbanFilter, setCheckIbanFilter] = useState<boolean | undefined>(
		undefined
	)
	const [filterName, setFilterName] = useState<undefined | string>(undefined)
	const [viewType, setViewType] = useState<undefined | string>("grid")

	const { ref, inView } = useInView({
		threshold: 1,
		triggerOnce: false,
	})

	const ButtonViewType = styled(Button)(({ theme }) => ({
		boxShadow:
			"0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)",
		"&:hover": {
			backgroundColor: theme.palette.secondary.contrastText,
			color: theme.palette.secondary.main,
			"& svg": {
				background: `${theme.palette.secondary.contrastText} !important`,
				color: `${theme.palette.secondary.main} !important`,
			},
		},
	}))

	const setFilters = useCallback(() => {
		const filter: { [key: string]: any } = {}

		if (filterName) filter.corporateName = filterName
		if (filterBank) filter.bankId = filterBank
		if (typeof autoAcceptFilter === "boolean")
			filter.autoAccept = autoAcceptFilter

		if (typeof whatsappFilter === "boolean") filter.whatsapp = whatsappFilter

		if (typeof checkIbanFilter === "boolean") filter.checkIban = checkIbanFilter

		return filter
	}, [
		filterName,
		filterBank,
		checkIbanFilter,
		autoAcceptFilter,
		whatsappFilter,
	])

	const handleSearch = () => {
		const filters: { [key: string]: any } = setFilters()
		const userSort = sortCriteria
			.map((criteria) => `${criteria.field}:${criteria.order}`)
			.join(",")
		dispatch(
			getCorporatesPaginated({
				page: 0,
				pageSize: 10,
				filter: filters,
				sort: userSort,
			})
		)
			.then(() => {})
			.catch(() => {})
	}

	useEffect(() => {
		setFilterBank(authState.user?.idBank ?? idBankForPaydoUser)
	}, [authState.user?.idBank, idBankForPaydoUser])

	useEffect(() => {
		if (isAuthenticate) {
			handleSearch()
		}
	}, [isAuthenticate])

	useEffect(() => {
		resetDefault()
	}, [])

	useEffect(() => {
		!hasCrudCorporateEnabled && navigate("/")
	}, [hasCrudCorporateEnabled])

	useEffect(() => {
		if (inView && corporatenextUrlState && !corporatesFetchLoadingState) {
			dispatch(fetchCorporates({ url: corporatenextUrlState }))
				.then(() => {})
				.catch(() => {})
		}
	}, [
		isAuthenticate,
		inView,
		corporatenextUrlState,
		corporatesFetchLoadingState,
	])

	return (
		<>
			<Paper
				component="form"
				onSubmit={(event: FormEvent) => {
					event.stopPropagation()
					event.preventDefault()
					handleSearch()
				}}
				sx={{
					p: "2px 4px",
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					width: "100%",
				}}
			>
				<InputBase
					value={filterName}
					onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
						setFilterName(event.target.value)
					}
					sx={{ ml: 1, flex: 1 }}
					placeholder={t("search.placeholder", { ns: "corporate" })}
					inputProps={{
						"aria-label": t("search.placeholder", { ns: "corporate" }),
					}}
				/>
				<IconButton
					type="button"
					sx={{ p: "10px" }}
					aria-label={t("search.accessibility.label", { ns: "corporate" })}
					onClick={() => handleSearch()}
				>
					<SearchIcon />
				</IconButton>
				<Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
				<Grid container gap="16px" sx={{ width: "fit-content" }}>
					<FiltersCorporate
						filterBank={filterBank}
						setFilterBank={setFilterBank}
						autoAcceptFilter={autoAcceptFilter}
						setAutoAcceptFilter={setAutoAcceptFilter}
						checkIbanFilter={checkIbanFilter}
						setCheckIbanFilter={setCheckIbanFilter}
						handleSearch={handleSearch}
						whatsappFilter={whatsappFilter}
						setWhatsappFilter={setWhatsappFilter}
					/>
					<Sort
						handleSearch={handleSearch}
						sortCriteria={sortCriteria}
						setSortCriteria={setSortCriteria}
						sortingOptions={[
							"id",
							"creationDate",
							"bankName",
							"enabled",
							"whatsapp",
						]}
						availableOptionsInitial={[
							"creationDate",
							"bankName",
							"enabled",
							"whatsapp",
						]}
					/>
				</Grid>
			</Paper>

			<Grid
				container
				alignItems={"center"}
				justifyContent={"flex-end"}
				width={"100%"}
				sx={{ marginTop: "16px" }}
			>
				<Tooltip title={t("view.accessibility.grid", { ns: "corporate" })}>
					<ButtonViewType
						variant={viewType === "grid" ? "contained" : "outlined"}
						aria-label={t("view.accessibility.grid", { ns: "corporate" })}
						onClick={() => setViewType("grid")}
						sx={{
							backgroundColor:
								viewType === "grid"
									? theme.palette.secondary.main
									: theme.palette.primary.main,
						}}
						startIcon={
							<GridViewIcon
								style={{
									backgroundColor:
										viewType === "grid"
											? theme.palette.secondary.main
											: theme.palette.primary.main,
									color:
										viewType === "grid"
											? theme.palette.secondary.contrastText
											: theme.palette.primary.contrastText,
								}}
							/>
						}
					></ButtonViewType>
				</Tooltip>
				<Tooltip title={t("view.accessibility.table", { ns: "corporate" })}>
					<ButtonViewType
						// variant={viewType === 'table' ? 'contained' : 'outlined'}
						aria-label={t("view.accessibility.table", { ns: "corporate" })}
						onClick={(e) => setViewType("table")}
						sx={{
							marginLeft: "16px",
							backgroundColor:
								viewType === "table"
									? theme.palette.secondary.main
									: theme.palette.primary.main,
						}}
						startIcon={
							<TocIcon
								style={{
									backgroundColor:
										viewType === "table"
											? theme.palette.secondary.main
											: theme.palette.primary.main,
									color:
										viewType === "table"
											? theme.palette.secondary.contrastText
											: theme.palette.primary.contrastText,
								}}
							/>
						}
					></ButtonViewType>
				</Tooltip>
			</Grid>
			{corporatesLoadingState ? (
				<SpinnerCard />
			) : (
				<>
					{viewType === "grid" ? (
						<CorporatesList />
					) : (
						<CorporateEnhancedTable
							sortCriteria={sortCriteria}
							setSortCriteria={setSortCriteria}
						/>
					)}
					{corporatesFetchLoadingState ? (
						<SpinnerCard />
					) : (
						<div ref={ref} style={{ height: "30px" }}></div>
					)}
				</>
			)}
		</>
	)
}

export default CorporatesListView
